import { isQaEnv } from '../utils';

const TWENTY_MB = 2e+7;
const isValidFileType = (file) => ['image/jpeg', 'image/png'].includes(file.type);
const isValidFileSize = (file) => file.size < TWENTY_MB;

const _loadScript = (src) => new Promise((resolve, reject) => {
  const scriptEl = document.createElement('script');

  scriptEl.async = true;
  scriptEl.addEventListener('load', resolve, { once: true });
  scriptEl.addEventListener('error', reject, { once: true });

  document.body.appendChild(scriptEl);

  scriptEl.src = src;
});

function _getImageUri(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

function _getLicenceKey() {
  const prodKey = 'FE828274012B2A155BB00592F258EE934DAF4FFF4A18D19BD54C05E745123A79EC5B0369BFA95361AF087288BFE22933A5B24ACD6607C2105F6FDAEEA3507CC2';
  const qaKey = 'DBC6C5F5255A55A0792C66B0A82A930AB3617147FBCD30CF7E7D8EF64FE0E8C189220DF9479F5030F105C31226398DC9B26572780F4FD53FCD2283E3EEF1A755';
  return isQaEnv() ? qaKey : prodKey;
}

function _getConfig(hostlink) {
  return {
    config: {
      moduleMode: 'Makeup',
      trackerLicenceKey: _getLicenceKey(),
      libraryInfo: {
        domain: hostlink.split(/https*:\/\//)[1],
        path: 'javascript/modiface6.0.0',
        version: '',
        maskPrefix: `${hostlink}/img/modiface6.0.0/`,
        height: 300,
        width: 300,
      },
    },
  };
}

async function _initSDK(hostlink) {
  await _loadScript(`${hostlink}/javascript/modiface6.0.0/sdk.js`);
  await window.MFE_VTO.init(_getConfig(hostlink));
}

function getOriginalCoordinates() {
  return window.MFE_VTO.getOriginalCoords();
}

async function setCoordinates(coordinates) {
  if (!coordinates) {
    throw new Error('Error: coordinates attr is required.');
  }

  // TODO: so strange modiface sdk issue
  // the coordinates change only on the second call of the function

  await window.MFE_VTO.setCoordinate(coordinates);
  await window.MFE_VTO.setCoordinate(coordinates);
}

function generatePhotoCanvas({ lookId = 'photo-canvas-after', lookObject = [] } = {}) {
  return window.MFE_VTO.setPhotoLook({ lookId, lookObject });
}

function generateLiveCanvas({ lookId = 'live-canvas-after', lookObject = [] } = {}) {
  return window.MFE_VTO.setLiveLook({ lookId, lookObject });
}

async function liveLook({ lookObject = [], hostlink }) {
  try {
    await _initSDK(hostlink);
    await window.MFE_VTO.startLiveMode();
    const { renderedCanvas: beforeRenderedCanvas } = await generateLiveCanvas({ lookId: 'live-canvas-before', lookObject: [] });
    const { renderedCanvas: afterRenderedCanvas } = await generateLiveCanvas({ lookId: 'live-canvas-after', lookObject });

    return { beforeRenderedCanvas, afterRenderedCanvas };
  } catch (error) {
    throw new Error(error);
  }
}

async function photoLook({ file, lookObject = [], hostlink }) {
  if (!isValidFileType(file)) {
    throw new Error('ERROR_FILETYPE: Image type unsupported. Please upload a .jpg or .png image.');
  }

  if (!isValidFileSize(file)) {
    throw new Error('ERROR_FILESIZE: Please upload an image less than 20mb.');
  }

  try {
    await _initSDK(hostlink);

    const base64Image = await _getImageUri(file);
    const adjustableCoords = await window.MFE_VTO.startPhotoMode({ imgUri: base64Image });
    const { renderedCanvas: beforeRenderedCanvas } = await generatePhotoCanvas({ lookId: 'photo-canvas-before', lookObject: [] });
    const { renderedCanvas: afterRenderedCanvas } = await generatePhotoCanvas({ lookId: 'photo-canvas-after', lookObject });

    return { adjustableCoords, beforeRenderedCanvas, afterRenderedCanvas };
  } catch (error) {
    throw new Error(error);
  }
}

async function destroyLiveLook() {
  await window.MFE_VTO.stopLiveMode();
  await window.MFE_VTO.destroyLiveModule();
}

async function destroyPhotoLook() {
  await window.MFE_VTO.destroyPhotoModule();
}

export {
  liveLook,
  photoLook,
  generatePhotoCanvas,
  generateLiveCanvas,
  getOriginalCoordinates,
  setCoordinates,
  destroyLiveLook,
  destroyPhotoLook,
};
